import { Injectable, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth.service';

/** One capability's usage tally. `lastUsed` is epoch ms. */
interface UsageEntry {
  count: number;
  lastUsed: number;
}

type UsageMap = Record<string, UsageEntry>;

const STORAGE_PREFIX = 'hc.concierge.usage.v1';
/** Keep the stored blob trivially small — the palette only ever shows a handful. */
const MAX_TRACKED = 20;
/** A tally decays to half its weight after this many days without use. */
const HALF_LIFE_DAYS = 30;
const DAY_MS = 86_400_000;

/**
 * Per-user "how often do you use this capability" tallies for the concierge
 * palette's Frequently-Used section.
 *
 * Deliberately localStorage-only: this is a menu-ordering convenience, not data.
 * If storage is empty, unreadable, or throws (Safari private mode, quota), the
 * palette silently falls back to its plain registry-order list — the feature is
 * absent, never an error. This service is the ONLY thing that touches the stored
 * counts, so moving them server-side later is a one-file change.
 *
 * The key is scoped by org AND user email because shared clinical workstations
 * are real — two people on one browser should not blend their lists.
 */
@Injectable({ providedIn: 'root' })
export class ConciergeUsageService {
  private auth = inject(AuthService);

  /** Signal-backed so the dialog's computed() re-runs when the async load lands. */
  private readonly usage = signal<UsageMap>({});

  private storageKey: string | null = null;
  /** Resolves once the email-scoped key is known and the map has been loaded. */
  private readonly ready: Promise<void>;

  constructor() {
    this.ready = this.resolveKeyAndLoad();
  }

  /**
   * Records one use. Fire-and-forget: if the key hasn't resolved yet the write
   * queues behind the same promise rather than being dropped.
   */
  record(capabilityId: string): void {
    if (!capabilityId) return;
    void this.ready.then(() => {
      const next: UsageMap = { ...this.usage() };
      const existing = next[capabilityId];
      next[capabilityId] = { count: (existing?.count ?? 0) + 1, lastUsed: Date.now() };
      const pruned = this.prune(next);
      this.usage.set(pruned);
      this.write(pruned);
    });
  }

  /** Capability ids, most-used first (recency-decayed). May include stale ids. */
  topIds(limit = 5): string[] {
    const map = this.usage();
    return Object.keys(map)
      .sort((a, b) => this.score(map[b]) - this.score(map[a]))
      .slice(0, limit);
  }

  /** How many distinct capabilities have ever been used (drives the show threshold). */
  distinctCount(): number {
    return Object.keys(this.usage()).length;
  }

  /**
   * Recency-decayed frequency. A burst of activity three months ago shouldn't
   * pin the list forever, but this is still "frequent", not "most recent".
   */
  private score(entry: UsageEntry | undefined): number {
    if (!entry) return 0;
    const daysSince = Math.max(0, (Date.now() - (entry.lastUsed || 0)) / DAY_MS);
    return (entry.count || 0) * Math.pow(0.5, daysSince / HALF_LIFE_DAYS);
  }

  private prune(map: UsageMap): UsageMap {
    const ids = Object.keys(map);
    if (ids.length <= MAX_TRACKED) return map;
    const keep = ids.sort((a, b) => this.score(map[b]) - this.score(map[a])).slice(0, MAX_TRACKED);
    const pruned: UsageMap = {};
    for (const id of keep) pruned[id] = map[id];
    return pruned;
  }

  private async resolveKeyAndLoad(): Promise<void> {
    let email = '';
    try {
      email = (await this.auth.getUserEmail()) || '';
    } catch {
      // Never let an auth hiccup break the palette — fall back to an unscoped key.
      email = '';
    }
    const org = (environment as any).org || 'unknown';
    const who = email.toLowerCase().trim() || 'anonymous';
    this.storageKey = `${STORAGE_PREFIX}.${org}.${who}`;
    this.usage.set(this.read());
  }

  private read(): UsageMap {
    if (!this.storageKey) return {};
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      // Defensive: drop anything that isn't a well-formed entry.
      const clean: UsageMap = {};
      for (const [id, entry] of Object.entries(parsed as Record<string, any>)) {
        const count = Number(entry?.count);
        const lastUsed = Number(entry?.lastUsed);
        if (Number.isFinite(count) && count > 0) {
          clean[id] = { count, lastUsed: Number.isFinite(lastUsed) ? lastUsed : 0 };
        }
      }
      return clean;
    } catch {
      return {};
    }
  }

  private write(map: UsageMap): void {
    if (!this.storageKey) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(map));
    } catch {
      // Quota / private mode — the in-memory map still serves this session.
    }
  }
}
